import type { FetchError, SalonStockerHttpTypes } from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import { useEffect } from "react";

import { queryKeys as productsQueryKeys } from "../products/queryKeys";
import { supplierMatchQueryKeys } from "./queryKeys";

/**
 * Client data layer for the buyer **Supplier Match** grid (ADR-0005):
 * `queryOptions` for the grid and a seller's Case B email state, and
 * mutations for auto-check, Retry, Release, and request-email. Confirming the
 * emailed confirmation link itself is NOT here - the public confirm page calls
 * `sdk.store.supplierMatch.confirmEmail` directly, mirroring `EnterPage.tsx`
 * (a fresh, often-unauthenticated tab with no live cache for this salon to
 * interact with).
 *
 * `useCheckSupplierMatches` writes its response directly into the grid's
 * cache rather than invalidating - the response already *is* the refreshed
 * grid (ADR-0005 "Status is persisted"), so a follow-up refetch would be a
 * wasted round trip.
 */

// ============================================================
// Queries
// ============================================================

/** The full grid - one card per active seller, a pure local read. */
export function supplierMatchListQuery() {
  return queryOptions({
    queryKey: supplierMatchQueryKeys.list(),
    queryFn: () => sdk.store.supplierMatch.list(),
    staleTime: 5 * 60 * 1000
  });
}

export function useSupplierMatches(options?: { enabled?: boolean }) {
  return useQuery({ ...supplierMatchListQuery(), ...options });
}

/**
 * A seller's Case B email state. Typically gated on a detail dialog being
 * open (`enabled`) rather than fired for every card on grid mount.
 */
export function supplierMatchSellerStateQuery(sellerId: string) {
  return queryOptions({
    queryKey: supplierMatchQueryKeys.sellerState(sellerId),
    queryFn: () => sdk.store.supplierMatch.getSellerState(sellerId)
  });
}

export function useSupplierMatchSellerState(
  sellerId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({ ...supplierMatchSellerStateQuery(sellerId), ...options });
}

/**
 * Refetch the grid each time `open` turns true (once per open, not on every
 * render), so a confirmation done in another tab or on another device shows
 * up in a detail dialog that is opened from a grid loaded long before. The
 * grid's cache is otherwise fresh for five minutes.
 */
export function useRefreshSupplierMatchesOnOpen(open: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      void queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
    }
  }, [open, queryClient]);
}

// ============================================================
// Mutations
// ============================================================

/**
 * Auto-check every not-yet-settled, lookup-capable seller. Idempotent - safe
 * to fire on every grid mount. The response is the refreshed grid, so it's
 * written straight into the list cache instead of triggering a refetch.
 */
export function useCheckSupplierMatches() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreSupplierMatchCheckResponse,
    FetchError,
    void
  >({
    mutationFn: () => sdk.store.supplierMatch.checkAll(),
    onSuccess: (data) => {
      queryClient.setQueryData<SalonStockerHttpTypes.StoreSupplierMatchListResponse>(
        supplierMatchQueryKeys.list(),
        { supplier_matches: data.supplier_matches }
      );
    }
  });
}

/** Force a fresh lookup for one seller, regardless of its current status. */
export function useRetrySupplierMatch() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreRetrySupplierMatchResponse,
    FetchError,
    string
  >({
    mutationFn: (sellerId) => sdk.store.supplierMatch.retry(sellerId),
    onSuccess: (_data, sellerId) => {
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
    }
  });
}

/** Release the salon's match for one seller - reverts checkout to the account email. */
export function useReleaseSupplierMatch() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse,
    FetchError,
    string
  >({
    mutationFn: (sellerId) =>
      sdk.store.supplierMatch.releaseSellerMatch(sellerId),
    onSuccess: (_data, sellerId) => {
      // Releasing a claim changes the prices the salon is quoted (KAN-343).
      // Reset, not invalidate: invalidating keeps the cached (now wrong) prices
      // on screen until the refetch lands.
      queryClient.resetQueries({
        queryKey: productsQueryKeys.productsPricingAll()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
    }
  });
}

export interface RequestSupplierMatchEmailInput {
  sellerId: string;
  email: string;
}

export interface ClaimSupplierMatchInput {
  sellerId: string;
  email: string;
}

/**
 * Claim a trade account with a claimable seller. Invalidates the seller's own
 * state and the grid - the card flips to `claimed` or `pending` at once.
 */
export function useClaimSupplierMatch() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreClaimSupplierMatchResponse,
    FetchError,
    ClaimSupplierMatchInput
  >({
    mutationFn: ({ sellerId, email }) =>
      sdk.store.supplierMatch.claim(sellerId, email),
    onSuccess: (_data, { sellerId }) => {
      // A claim changes the prices the salon is quoted (KAN-343). Reset, not
      // invalidate: invalidating keeps the cached retail prices on screen until
      // the refetch lands; resetting drops them so the loader shows.
      queryClient.resetQueries({
        queryKey: productsQueryKeys.productsPricingAll()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
    }
  });
}

/**
 * Request an alternate lookup email for one seller (Case B). Invalidates
 * both the seller's own state AND the grid - a request can change the
 * grid's `pending_email` immediately, before any lookup runs.
 */
export function useRequestSupplierMatchEmail() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse,
    FetchError,
    RequestSupplierMatchEmailInput
  >({
    mutationFn: ({ sellerId, email }) =>
      sdk.store.supplierMatch.requestSellerEmail(sellerId, email),
    onSuccess: (_data, { sellerId }) => {
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
    }
  });
}
